import moment from "moment-timezone";

import { OrderTotalsResult } from "src/core/dashboard/api/widget-data.api";

import {
  DisplayRangeType,
  groupByDisplayRange
} from "../../utils";
import { OrderTotalsGraphValue } from "../OrderTotalsWidget";
import { GraphDataType } from "../../WidgetTotalValueSwitcher";

/**
 * Helper to group order totals
 *
 * @param orderTotals A list of flat order totals
 * @param displayRange DisplayRangeType
 * @returns
 *
 * ### Example.
 *
 * **In:**
 * ```
 * const orderTotals = [
 *  {
      "date": "2022-01-31T00:00:00.000Z",
      "count": 1,
      "value": 8.509999871253967
    },
    {
      "date": "2022-01-31T00:00:00.000Z",
      "count": 1,
      "value": 8.509999871253967
    },
    {
      "date": "Thu Feb 03 2022 00:00:00",
      "count": 1,
      "value": 8.509999871253967
    }
 * ]
 * const displayRange = "month"
 * ```
 * **Out:**
 * ```
 *[
    {
      "date": "2022-01-01T00:00:00.000Z",
      "value": 17.019999742507935
    },
    {
      "date": "2022-02-01T00:00:00.000Z",
      "value": 8.509999871253967
    }
  ]
 * ```
 */
export const groupOrderTotals = (orderTotals: OrderTotalsResult, displayRange: DisplayRangeType, graphDataType: GraphDataType): OrderTotalsGraphValue[] => {
  const result: OrderTotalsGraphValue[] = [];
  // Group the data based on displayRange
  const groupedOrderTotalsData = groupByDisplayRange(orderTotals, displayRange);

  // Loop grouped data to form the result, ensuring to include date and all relevant values accumulated
  for (const groupDate in groupedOrderTotalsData) {
    const accumulativeTotals = { value: 0 };

    groupedOrderTotalsData[ groupDate ].forEach(row => {
      accumulativeTotals.value += graphDataType === "total" ? row.count : row.value;
    });

    const dataItem: OrderTotalsGraphValue = {
      date: moment.utc(groupDate).toDate(),
      ...accumulativeTotals
    };

    // Force header to include date day if displayRange not by month
    if (displayRange !== "month") {
      dataItem.toolTipHeader = moment.utc(groupDate).format("Do MMMM YYYY");
    }

    result.push(dataItem);
  }

  return result;
};
